import { useEffect, useMemo, useState } from "react";
import Snowfall from "react-snowfall";
import { useTheme } from "./useTheme";
import lanternImg from "../assets/images/chinese-lantern.png";
import branchImg from "../assets/images/chinese-newyear-tree.png";
import firecrackerImg from "../assets/images/chinese-firecracker.png";
import dragonImg from "../assets/images/dragon_1.png";
import lionImg from "../assets/images/chinese-lion.png";

/** Spawn N positioned children with random delays/durations to feel "alive". */
function useParticles(count: number, seed: number = 0) {
  return useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        // Pseudo-random but stable across renders
        const r = (n: number) => ((Math.sin((i + 1) * (seed + 9301)) * 43758.5453) % 1 + 1) % 1 * n;
        return {
          left: r(100),                        // %
          delay: r(8),                         // s
          duration: 6 + r(12),                 // s
          size: 14 + r(28),                    // px
          drift: -10 + r(20),                  // % (horizontal sway)
        };
      }),
    [count, seed]
  );
}

/* ============== Christmas dressing ============== */

const BULB_COLORS = ["#FF3B30", "#FFD60A", "#34C759", "#0A84FF", "#FFFFFF", "#FF6B00"];

// Christmas wreath SVG
const Wreath: React.FC = () => (
  <svg className="xmas-wreath" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    {/* foliage ring (many tiny leaves around a circle) */}
    <g>
      {Array.from({ length: 14 }).map((_, i) => {
        const ang = (i / 14) * 2 * Math.PI;
        const cx = 50 + Math.cos(ang) * 32;
        const cy = 50 + Math.sin(ang) * 32;
        return <circle key={i} cx={cx} cy={cy} r={11} fill="#0a5c3e" />;
      })}
      {Array.from({ length: 14 }).map((_, i) => {
        const ang = ((i + 0.5) / 14) * 2 * Math.PI;
        const cx = 50 + Math.cos(ang) * 32;
        const cy = 50 + Math.sin(ang) * 32;
        return <circle key={`b${i}`} cx={cx} cy={cy} r={9} fill="#0d7a52" />;
      })}
    </g>
    {/* Berries */}
    {[0, 1.5, 3.0, 4.5].map((a, i) => (
      <circle key={`berry${i}`} cx={50 + Math.cos(a) * 30} cy={50 + Math.sin(a) * 30} r="3" fill="#C8102E" />
    ))}
    {/* Bow */}
    <g transform="translate(50 78)">
      <path d="M-12 0 Q-6 -8 0 0 Q6 -8 12 0 Q6 4 0 1 Q-6 4 -12 0 Z" fill="#C8102E" />
      <rect x="-2" y="-3" width="4" height="14" fill="#9b0c24" rx="1" />
    </g>
  </svg>
);

const Bulbs = Array.from({ length: 28 });

const ChristmasDress: React.FC = () => {
  const [welcomeOpen, setWelcomeOpen]   = useState(false);
  const [welcomeLeaving, setLeaving]    = useState(false);

  // Show welcome modal once per browser session when Christmas theme is on.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("xmas.welcome.seen") === "1") return;
      sessionStorage.setItem("xmas.welcome.seen", "1");
      setWelcomeOpen(true);
      const t1 = setTimeout(() => setLeaving(true), 3200);
      const t2 = setTimeout(() => setWelcomeOpen(false), 3700);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } catch { /* ignore */ }
  }, []);

  return (
    <>
      {/* Twinkling lights at top */}
      <div className="xmas-lights">
        {Bulbs.map((_, i) => (
          <span
            key={i}
            className="xmas-bulb"
            style={{
              background: BULB_COLORS[i % BULB_COLORS.length],
              color: BULB_COLORS[i % BULB_COLORS.length],
              animationDelay: `${(i % 6) * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Wreath top-right */}
      <Wreath />


      {/* Snow pile at bottom + reindeer tracks */}
      <div className="xmas-snow-pile" aria-hidden />
      <div className="xmas-tracks" aria-hidden />

      {/* First-visit welcome modal */}
      {welcomeOpen && (
        <div className={`xmas-welcome ${welcomeLeaving ? "leaving" : ""}`} role="status" aria-live="polite">
          <div className="bg-white rounded-3xl px-8 py-7 shadow-floaty border-4 text-center max-w-[340px]"
               style={{ borderColor: "#C8102E" }}>
            <div className="text-5xl mb-2">🎄✨🎁</div>
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: "#006847" }}>
              Tis the season
            </p>
            <h2 className="font-display font-black text-2xl mt-1 leading-tight" style={{ color: "#C8102E" }}>
              Merry Christmas
            </h2>
            <p className="text-sm text-ink-700 mt-2">
              from everyone at <span className="font-bold">Woxa Cafe</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

/* ============== Welcome modal helper ============== */

interface WelcomeProps {
  storageKey: string;
  emojis: string;
  eyebrow: string;
  eyebrowColor: string;
  title: string;
  titleColor: string;
  subtitle: string;
  borderColor: string;
}

const Welcome: React.FC<WelcomeProps> = ({ storageKey, emojis, eyebrow, eyebrowColor, title, titleColor, subtitle, borderColor }) => {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(storageKey) === "1") return;
      sessionStorage.setItem(storageKey, "1");
      setOpen(true);
      const t1 = setTimeout(() => setLeaving(true), 3200);
      const t2 = setTimeout(() => setOpen(false), 3700);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } catch { /* ignore */ }
  }, [storageKey]);
  if (!open) return null;
  return (
    <div className={`xmas-welcome ${leaving ? "leaving" : ""}`} role="status" aria-live="polite">
      <div className="bg-white rounded-3xl px-8 py-7 shadow-floaty border-4 text-center max-w-[340px]" style={{ borderColor }}>
        <div className="text-5xl mb-2">{emojis}</div>
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: eyebrowColor }}>{eyebrow}</p>
        <h2 className="font-display font-black text-2xl mt-1 leading-tight" style={{ color: titleColor }}>{title}</h2>
        <p className="text-sm text-ink-700 mt-2">{subtitle}</p>
      </div>
    </div>
  );
};

/* ============== per-theme dressings ============== */

const ValentineDress: React.FC = () => {
  const items = useParticles(22, 1);
  return (
    <>
      {items.map((p, i) => (
        <span key={i} aria-hidden className="fx-float-up"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            fontSize: `${p.size}px`,
            // @ts-ignore
            "--drift": `${p.drift}vw`,
          }}
        >
          {i % 4 === 0 ? "🌹" : i % 4 === 1 ? "💕" : i % 4 === 2 ? "❤️" : "💖"}
        </span>
      ))}
      {/* Heart garland top */}
      <div className="fx-heart-garland" aria-hidden>
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} style={{ animationDelay: `${(i % 5) * 0.2}s` }}>💗</span>
        ))}
      </div>
      <Welcome
        storageKey="theme.valentine.welcome"
        emojis="💖🌹💕"
        eyebrow="Love is in the air"
        eyebrowColor="#9D174D"
        title="Happy Valentine's"
        titleColor="#E91E63"
        subtitle="from everyone at Woxa Cafe"
        borderColor="#E91E63"
      />
    </>
  );
};

const SongkranDress: React.FC = () => {
  const items = useParticles(50, 2);
  return (
    <>
      {items.map((p, i) => (
        <span key={i} aria-hidden className="fx-fall"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${2 + p.duration / 6}s`,
            fontSize: `${p.size * 0.65}px`,
          }}
        >
          {i % 5 === 0 ? "🌸" : i % 5 === 1 ? "💦" : i % 5 === 2 ? "💧" : i % 5 === 3 ? "🪷" : "🌊"}
        </span>
      ))}
      {/* Jasmine garland top */}
      <div className="fx-jasmine-garland" aria-hidden>
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i}>{i % 4 === 0 ? "🌼" : "🌸"}</span>
        ))}
      </div>
      <Welcome
        storageKey="theme.songkran.welcome"
        emojis="💦🌸🪷"
        eyebrow="สงกรานต์"
        eyebrowColor="#0369A1"
        title="Sawatdee Pii Mai"
        titleColor="#0EA5E9"
        subtitle="Happy Songkran from Woxa Cafe"
        borderColor="#0EA5E9"
      />
    </>
  );
};

const ChineseNewYearDress: React.FC = () => {
  const items = useParticles(36, 5);
  return (
    <>
      {/* Falling petals + gold sparks + red envelopes */}
      {items.map((p, i) => (
        <span key={i} aria-hidden className="fx-fall"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${3.5 + p.duration / 4}s`,
            fontSize: `${p.size * 0.75}px`,
          }}
        >
          {i % 5 === 0 ? "🧧" : i % 5 === 1 ? "🏮" : i % 5 === 2 ? "✨" : i % 5 === 3 ? "🌸" : "🪙"}
        </span>
      ))}
      {/* Plum-blossom branch with hanging lanterns at top */}
      <img src={branchImg} alt="" aria-hidden className="fx-cny-branch" />
      {/* Hanging lanterns in top corners */}
      <img src={lanternImg} alt="" aria-hidden className="fx-lantern fx-lantern-l" />
      <img src={lanternImg} alt="" aria-hidden className="fx-lantern fx-lantern-r" />
      {/* Bursting firecracker bottom-right */}
      <img src={firecrackerImg} alt="" aria-hidden className="fx-firecracker" />
      {/* Dragon drifting bottom-left */}
      <img src={dragonImg} alt="" aria-hidden className="fx-dragon" />
      {/* Welcome card features the lion dance illustration */}
      <CnyWelcome />
    </>
  );
};

const CnyWelcome: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("theme.cny.welcome") === "1") return;
      sessionStorage.setItem("theme.cny.welcome", "1");
      setOpen(true);
      const t1 = setTimeout(() => setLeaving(true), 3400);
      const t2 = setTimeout(() => setOpen(false), 3900);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } catch { /* ignore */ }
  }, []);
  if (!open) return null;
  return (
    <div className={`xmas-welcome ${leaving ? "leaving" : ""}`} role="status" aria-live="polite">
      <div className="bg-white rounded-3xl px-8 py-7 shadow-floaty border-4 text-center max-w-[340px]" style={{ borderColor: "#D4181C" }}>
        <img src={lionImg} alt="" aria-hidden className="mx-auto mb-2" style={{ height: 96, width: "auto" }} />
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: "#8B0A0E" }}>新年快乐</p>
        <h2 className="font-display font-black text-2xl mt-1 leading-tight" style={{ color: "#D4181C" }}>Happy Chinese New Year</h2>
        <p className="text-sm text-ink-700 mt-2">Gōng xǐ fā cái — from <span className="font-bold">Woxa Cafe</span></p>
      </div>
    </div>
  );
};

const NewYearDress: React.FC = () => {
  const items = useParticles(60, 4);
  const year = new Date().getFullYear();
  return (
    <>
      {items.map((p, i) => (
        <span key={i} aria-hidden className="fx-fall"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${3 + p.duration / 4}s`,
            fontSize: `${p.size * 0.55}px`,
          }}
        >
          {i % 6 === 0 ? "✨" : i % 6 === 1 ? "🥂" : i % 6 === 2 ? "🎉" : i % 6 === 3 ? "⭐" : i % 6 === 4 ? "🪅" : "🎊"}
        </span>
      ))}
      {/* Confetti streamers from top corners */}
      <span className="fx-streamer fx-streamer-l" aria-hidden />
      <span className="fx-streamer fx-streamer-r" aria-hidden />
      <Welcome
        storageKey={`theme.newyear.welcome.${year}`}
        emojis="🥂🎉✨"
        eyebrow={`Welcome ${year + 1}`}
        eyebrowColor="#A8861F"
        title="Happy New Year"
        titleColor="#C9A227"
        subtitle="from everyone at Woxa Cafe"
        borderColor="#C9A227"
      />
    </>
  );
};

/* ============== Wrapper ============== */

const ThemeFx: React.FC = () => {
  const [theme] = useTheme();

  // Halloween is dressed inline, never over the ordering controls.
  if (theme === "default" || theme === "halloween") return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden">
      {theme === "christmas" && (
        <>
          <Snowfall snowflakeCount={180} color="#ffffff" speed={[1.0, 3.0]} wind={[-1, 1.5]} radius={[1.2, 5]} />
          <ChristmasDress />
        </>
      )}
      {theme === "songkran"  && <SongkranDress />}
      {theme === "valentine" && <ValentineDress />}
      {theme === "newyear"   && <NewYearDress />}
      {theme === "cny"       && <ChineseNewYearDress />}
    </div>
  );
};

export default ThemeFx;
